import type { Client } from '@accounts/transport';

/** A hand-off to an installed client on this computer, waiting for the person (`/consent`) */
export interface Consent {
	product: { id: string; name: string };
	person: { name: string | null };
	/** The installed client's address, `http://127.0.0.1:<port>`, for the technical details */
	address: string;
	expires: string;
}

/**
 * Opening a product without signing in again.
 *
 * This is the browser's half of the exchange, and it is deliberately the smaller half. The browser
 * asks for a one-time code and navigates to the product with it; the product's own server redeems it
 * with a verifier the browser never sees. A code caught in a history entry or a referrer is therefore
 * not a session.
 *
 * The verifier is generated by the product, not here. What this class must not do is invent one, which
 * would put both halves of the proof in the same place and make the exchange decorative.
 */
export class Handoff {
	#client: Client;

	constructor(client: Client) {
		this.#client = client;
	}

	/**
	 * @param challenge The digest the product published when it sent the person here
	 * @returns The destination to navigate to, with the code attached
	 */
	async open(specs: { product: string; destination: string; challenge: string; organization?: string }): Promise<string> {
		const issued = await this.#client.post<{ code: string; destination: string }>('/v1/exchange/codes', specs);
		const url = new URL(issued.destination);
		url.searchParams.set('code', issued.code);
		return url.toString();
	}

	/**
	 * What a hand-off to an installed client asks the person: the product and who it will act as. The
	 * ticket is the service's, bound to this session, and reaches nothing but this read and `decide`.
	 */
	async consent(ticket: string): Promise<Consent> {
		return this.#client.get<Consent>(`/v1/authorize/consent?ticket=${encodeURIComponent(ticket)}`);
	}

	/**
	 * Continue (`true`) or Cancel (`false`), once. Never sent again after a renewal: the person decides
	 * again, on a page that reads the consent afresh.
	 *
	 * @returns The address the service answered, the only one this interface goes to
	 */
	async decide(ticket: string, allowed: boolean): Promise<string> {
		const decided = await this.#client.post<{ destination: string }>('/v1/authorize/consent', { ticket, allowed }, { replay: false });
		return decided.destination;
	}
}
